import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCallback } from 'react';

const LAST_VIEWED_KEY = 'friend_feed_last_viewed';

export function useFriendFeedUnread() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get the last viewed timestamp from localStorage
  const getLastViewed = (): Date | null => {
    const stored = localStorage.getItem(LAST_VIEWED_KEY);
    return stored ? new Date(stored) : null;
  };

  // Mark as viewed (call when user visits the friend feed)
  const markAsViewed = useCallback(() => {
    localStorage.setItem(LAST_VIEWED_KEY, new Date().toISOString());
    queryClient.setQueryData(['friend-feed-unread', user?.id], false);
  }, [queryClient, user?.id]);

  // Check if there's new activity since last viewed
  const { data: hasUnread = false } = useQuery({
    queryKey: ['friend-feed-unread', user?.id],
    queryFn: async () => {
      if (!user) return false;

      const lastViewed = getLastViewed();
      
      // If user has never viewed the feed, don't show notification
      // They'll see everything as "new" when they first visit
      if (!lastViewed) return false;
      
      // Get users the current user is following
      const { data: follows } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);

      if (!follows || follows.length === 0) return false;

      const followingIds = follows.map(f => f.following_id);
      
      const since = lastViewed.toISOString();
      
      // Check polls
      const { count: pollCount } = await supabase
        .from('polls')
        .select('id', { count: 'exact', head: true })
        .in('user_id', followingIds)
        .eq('visibility', 'public')
        .gt('created_at', since);

      if (pollCount && pollCount > 0) return true;

      // Check collections
      const { count: collectionCount } = await supabase
        .from('collections')
        .select('id', { count: 'exact', head: true })
        .in('user_id', followingIds)
        .eq('is_public', true)
        .gt('created_at', since);

      return (collectionCount && collectionCount > 0) || false;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });

  return { hasUnread, markAsViewed };
}
